import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  TrendingUp as TrendingIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as PorCobrarIcon,
  AssignmentReturn as DevolucionesIcon,
  SwapHoriz as IntercambiosIcon,
  CalendarToday,
} from '@mui/icons-material';
import {
  DashboardLocationProvider,
  useDashboardLocation,
} from '../contexts/DashboardLocationContext';
import { KPIBar } from '../components/dashboard/KPIBar';
import {
  DashboardDateRangePicker,
  DateRangeValue,
  getRangeFromPreset,
} from '../components/dashboard/DashboardDateRangePicker';
import { SalesTrendChart } from '../components/dashboard/SalesTrendChart';
import { OrdersTrendChart } from '../components/dashboard/OrdersTrendChart';
import { SalesUnitsChart } from '../components/dashboard/SalesUnitsChart';
import { MarginTrendChart } from '../components/dashboard/MarginTrendChart';
import { DevolucionesTrendChart } from '../components/dashboard/DevolucionesTrendChart';
import { TopProductsChart } from '../components/dashboard/TopProductsChart';
import { StockAvailableByLocationChart } from '../components/dashboard/StockAvailableByLocationChart';
import { StockIngressTrendChart } from '../components/dashboard/StockIngressTrendChart';
import { InventoryStatusChart } from '../components/dashboard/InventoryStatusChart';
import { InventoryTransfersChart } from '../components/dashboard/InventoryTransfersChart';
import { InventoryRecordsChart } from '../components/dashboard/InventoryRecordsChart';
import { OrdersStatusChart } from '../components/dashboard/OrdersStatusChart';
import { DashboardScopeTabs } from '../components/dashboard/DashboardScopeTabs';
import { KPIConfig } from '../types/kpi.types';
import { DashboardService } from '../api/services/DashboardService';
import type { DashboardKpiItemDto } from '../api/models/DashboardKpiItemDto';
import type { DashboardMoneyPointDto } from '../api/models/DashboardMoneyPointDto';
import type { DashboardUnitsPointDto } from '../api/models/DashboardUnitsPointDto';
import type { DashboardDevolucionesPointDto } from '../api/models/DashboardDevolucionesPointDto';
import type { DashboardMarginPointDto } from '../api/models/DashboardMarginPointDto';
import type { DashboardStockIngressPointDto } from '../api/models/DashboardStockIngressPointDto';
import type { DashboardStockByLocationPointDto } from '../api/models/DashboardStockByLocationPointDto';
import type { DashboardStatusPointDto } from '../api/models/DashboardStatusPointDto';
import type { DashboardTopProductDto } from '../api/models/DashboardTopProductDto';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { chartPalette } from '../components/dashboard/chartPalette';
import { useColorMode } from '../context/ColorModeContext';

const buildDefaultKpis = (): KPIConfig[] => [
  {
    id: 'sales',
    title: 'Ventas de Hoy',
    value: 0,
    icon: <MoneyIcon />,
    variant: 'primary',
    format: 'currency',
    trend: { value: 0, isPositive: true, label: 'vs el día de ayer' },
  },
  {
    id: 'units',
    title: 'Piezas vendidas',
    value: 0,
    icon: <CartIcon />,
    variant: 'success',
    format: 'number',
    trend: { value: 0, isPositive: true, label: 'vs el día de ayer' },
  },
  {
    id: 'ticket',
    title: 'Ticket Promedio',
    value: 0,
    icon: <TrendingIcon />,
    variant: 'info',
    format: 'currency',
    trend: { value: 0, isPositive: true, label: 'vs el día de ayer' },
  },
  {
    id: 'orders',
    title: 'Ventas de hoy',
    value: 0,
    icon: <ReceiptIcon />,
    variant: 'violet',
    format: 'number',
    trend: { value: 0, isPositive: true, label: 'vs el día de ayer' },
  },
  {
    id: 'porCobrar',
    title: 'Por cobrar',
    value: 0,
    icon: <PorCobrarIcon />,
    variant: 'amber',
    format: 'currency',
  },
  {
    id: 'devoluciones',
    title: '',
    value: 0,
    icon: <DevolucionesIcon />,
    variant: 'slate',
    format: 'breakdown',
    breakdown: [
      { label: 'Devuelto', value: 0, orderCount: 0, color: chartPalette.status.error },
      { label: 'Retenido', value: 0, orderCount: 0, color: chartPalette.status.success },
    ],
  },
  {
    id: 'intercambios',
    title: 'Intercambios',
    value: 0,
    icon: <IntercambiosIcon />,
    variant: 'info',
    format: 'number',
    trend: { value: 0, isPositive: true, label: 'vs el día de ayer' },
  },
];

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const dateRangeToApiParams = (range: DateRangeValue): { from: string; to: string } => ({
  from: startOfDay(range.startDate).toISOString(),
  to: endOfDay(range.endDate).toISOString(),
});

const defaultDateRange = (): DateRangeValue => {
  const { startDate, endDate } = getRangeFromPreset('last7');
  return { startDate, endDate, presetId: 'last7' };
};

function getRangeLabel(value: DateRangeValue): string {
  if (value.presetId === 'custom') {
    return `${format(value.startDate, 'd MMM', { locale: es })} - ${format(value.endDate, 'd MMM yyyy', { locale: es })}`;
  }
  const labels: Record<string, string> = {
    last7: 'Últimos 7 días',
    last30: 'Últimos 30 días',
    last90: 'Últimos 90 días',
    last365: 'Últimos 365 días',
    thisWeek: 'Esta semana',
    thisMonth: 'Este mes',
    thisYear: 'Este año',
    lastMonth: 'El mes pasado',
  };
  const label = labels[value.presetId] ?? value.presetId;
  return `${label} (${format(value.startDate, 'd MMM', { locale: es })} - ${format(value.endDate, 'd MMM', { locale: es })})`;
}

const mapKpisToUi = (items: DashboardKpiItemDto[] | undefined): KPIConfig[] => {
  const fallback = buildDefaultKpis();
  const byId = new Map((items ?? []).map((item) => [item.id, item]));

  return fallback.map((base) => {
    const source = byId.get(base.id);
    if (source == null) return base;
    return {
      ...base,
      value: source.value,
      format:
        (source as { format?: string }).format === 'breakdown'
          ? 'breakdown'
          : (source as { format?: string }).format === 'currency'
            ? 'currency'
            : 'number',
      ...((
        source as {
          breakdown?: { label: string; value: number; orderCount?: number; color?: string }[];
        }
      ).breakdown != null
        ? {
            breakdown: (
              source as {
                breakdown?: { label: string; value: number; orderCount?: number; color?: string }[];
              }
            ).breakdown,
          }
        : {}),
      ...(source.trend != null
        ? {
            trend: {
              value: source.trend.value ?? 0,
              isPositive: source.trend.isPositive ?? true,
              label: source.trend.label ?? 'vs el día de ayer',
            },
          }
        : {}),
    };
  });
};

const PageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode: 'light' | 'dark' }>(({ theme, mode }) => ({
  paddingTop: '32px',
  paddingBottom: '32px',
  paddingLeft: '32px',
  paddingRight: '32px',
  minHeight: '100vh',
  width: '100%',
  ...(mode === 'light'
    ? {
        // Fondo celeste muy suave para modo claro
        background: '#f0f9ff',
        color: theme.palette.text.primary,
      }
    : {
        // Fondo oscuro degradado original
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        color: '#f9fafb',
      }),
}));

const DashboardPageContent: React.FC = () => {
  const { mode } = useColorMode();
  const theme = useTheme();
  const { scope } = useDashboardLocation();
  const isGlobal = scope === 'global';
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [kpis, setKpis] = useState<KPIConfig[]>(buildDefaultKpis);
  const [salesMoneyData, setSalesMoneyData] = useState<DashboardMoneyPointDto[]>([]);
  const [salesUnitsData, setSalesUnitsData] = useState<DashboardUnitsPointDto[]>([]);
  const [marginTrendData, setMarginTrendData] = useState<DashboardMarginPointDto[]>([]);
  const [stockIngressData, setStockIngressData] = useState<DashboardStockIngressPointDto[]>([]);
  const [stockByLocationData, setStockByLocationData] = useState<
    DashboardStockByLocationPointDto[]
  >([]);
  const [inventoryStatusData, setInventoryStatusData] = useState<DashboardStatusPointDto[]>([]);
  const [inventoryTransfersData, setInventoryTransfersData] = useState<DashboardStatusPointDto[]>(
    [],
  );
  const [inventoryRecordsData, setInventoryRecordsData] = useState<DashboardStatusPointDto[]>([]);
  const [ordersStatusData, setOrdersStatusData] = useState<DashboardStatusPointDto[]>([]);
  const [topProductsData, setTopProductsData] = useState<DashboardTopProductDto[]>([]);
  const [devolucionesTrendData, setDevolucionesTrendData] = useState<
    DashboardDevolucionesPointDto[]
  >([]);

  const [dateRange, setDateRange] = useState<DateRangeValue>(defaultDateRange);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const handleOpenDatePicker = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(e.currentTarget);
  }, []);
  const handleCloseDatePicker = useCallback(() => {
    setDatePickerAnchor(null);
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('dashboard-mode');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const scopeParam = scope === 'global' ? 'global' : String(scope);
    const { from, to } = dateRangeToApiParams(dateRange);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

    const today = new Date();
    const kpisFrom = startOfDay(today).toISOString();
    const kpisTo = endOfDay(today).toISOString();

    const loadDashboard = async () => {
      setDashboardLoading(true);
      try {
        const [
          kpisResponse,
          moneyTrendResponse,
          unitsTrendResponse,
          marginTrendResponse,
          devolucionesTrendResponse,
          ingressTrendResponse,
          stockByLocationResponse,
          inventoryStatusResponse,
          transfersStatusResponse,
          recordsStatusResponse,
          ordersStatusResponse,
          topProductsResponse,
        ] = await Promise.all([
          DashboardService.dashboardControllerGetKpis(scopeParam, kpisFrom, kpisTo, tz),
          DashboardService.dashboardControllerGetSalesMoneyTrend(scopeParam, from, to, tz, 'day'),
          DashboardService.dashboardControllerGetSalesUnitsTrend(scopeParam, from, to, tz, 'day'),
          DashboardService.dashboardControllerGetMarginTrend(scopeParam, from, to, tz, 'day'),
          DashboardService.dashboardControllerGetDevolucionesTrend(scopeParam, from, to, tz, 'day'),
          DashboardService.dashboardControllerGetStockIngressTrend(scopeParam, from, to, tz, 'day'),
          DashboardService.dashboardControllerGetStockByLocation(
            scopeParam,
            new Date().toISOString(),
          ),
          DashboardService.dashboardControllerGetInventoryStatus(
            scopeParam,
            new Date().toISOString(),
          ),
          DashboardService.dashboardControllerGetInventoryTransfersStatus(scopeParam, from, to, tz),
          DashboardService.dashboardControllerGetInventoryRecordsStatus(scopeParam, from, to, tz),
          DashboardService.dashboardControllerGetOrdersStatus(scopeParam, from, to, tz),
          DashboardService.dashboardControllerGetTopProducts(scopeParam, from, to, tz),
        ]);

        if (cancelled) return;
        setKpis(mapKpisToUi(kpisResponse?.kpis));
        setSalesMoneyData(moneyTrendResponse ?? []);
        setSalesUnitsData(unitsTrendResponse ?? []);
        setMarginTrendData(marginTrendResponse ?? []);
        setDevolucionesTrendData(devolucionesTrendResponse ?? []);
        setStockIngressData(ingressTrendResponse ?? []);
        setStockByLocationData(stockByLocationResponse ?? []);
        setInventoryStatusData(inventoryStatusResponse ?? []);
        setInventoryTransfersData(transfersStatusResponse ?? []);
        setInventoryRecordsData(recordsStatusResponse ?? []);
        setOrdersStatusData(ordersStatusResponse ?? []);
        setTopProductsData(topProductsResponse ?? []);
      } catch (error) {
        if (cancelled) return;
        // Keep UI stable with safe defaults; no realtime retry loop by design.
        setKpis(buildDefaultKpis());
        setSalesMoneyData([]);
        setSalesUnitsData([]);
        setMarginTrendData([]);
        setDevolucionesTrendData([]);
        setStockIngressData([]);
        setStockByLocationData([]);
        setInventoryStatusData([]);
        setInventoryTransfersData([]);
        setInventoryRecordsData([]);
        setOrdersStatusData([]);
        setTopProductsData([]);
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };

    void loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [scope, dateRange]);

  return (
    <PageContainer mode={mode}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Panel de control
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <DashboardDateRangePicker
            value={dateRange}
            onChange={setDateRange}
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={handleCloseDatePicker}
            mode={mode}
            trigger={
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenDatePicker}
                startIcon={<CalendarToday />}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  ...(mode === 'light'
                    ? {
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.light,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                      }
                    : {
                        color: alpha(theme.palette.common.white, 0.85),
                        borderColor: alpha(theme.palette.common.white, 0.2),
                        '&:hover': {
                          borderColor: alpha(theme.palette.common.white, 0.4),
                          bgcolor: alpha(theme.palette.common.white, 0.05),
                        },
                      }),
                }}
              >
                {getRangeLabel(dateRange)}
              </Button>
            }
          />
        </Box>
      </Box>
      <KPIBar
        kpis={kpis}
        loading={dashboardLoading}
        mode={mode}
        columns={{ xs: 7, sm: 7, md: 7, lg: 7 }}
      />
      <DashboardScopeTabs mode={mode} />

      {/* Gráficos de ventas */}
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <SalesTrendChart mode={mode} loading={dashboardLoading} data={salesMoneyData} />
        <OrdersTrendChart mode={mode} loading={dashboardLoading} data={salesMoneyData} />
        <SalesUnitsChart mode={mode} loading={dashboardLoading} data={salesUnitsData} />
        <MarginTrendChart mode={mode} loading={dashboardLoading} data={marginTrendData} />
        <DevolucionesTrendChart
          mode={mode}
          loading={dashboardLoading}
          data={devolucionesTrendData}
        />
        <TopProductsChart mode={mode} loading={dashboardLoading} data={topProductsData} />
      </Box>

      {/* Histórico de stock ingresado */}
      <Box sx={{ mt: 4 }}>
        <StockIngressTrendChart mode={mode} loading={dashboardLoading} data={stockIngressData} />
      </Box>

      {/* Stock disponible por sucursal/almacén - solo en vista Global */}
      {isGlobal && (
        <Box sx={{ mt: 4 }}>
          <StockAvailableByLocationChart
            mode={mode}
            loading={dashboardLoading}
            data={stockByLocationData}
          />
        </Box>
      )}

      {/* Gráficos de inventario y órdenes - estado, transferencias, registro, órdenes por estado */}
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr' },
          gap: 3,
        }}
      >
        <InventoryRecordsChart mode={mode} loading={dashboardLoading} data={inventoryRecordsData} />
        <InventoryTransfersChart
          mode={mode}
          loading={dashboardLoading}
          data={inventoryTransfersData}
        />
        <InventoryStatusChart mode={mode} loading={dashboardLoading} data={inventoryStatusData} />
        <OrdersStatusChart mode={mode} loading={dashboardLoading} data={ordersStatusData} />
      </Box>

      {/* Resto del dashboard vendrá después */}
      <Box sx={{ mt: 4 }}>{/* Aquí irán los otros bloques */}</Box>
    </PageContainer>
  );
};

const DashboardPage: React.FC = () => (
  <DashboardLocationProvider>
    <DashboardPageContent />
  </DashboardLocationProvider>
);

export default DashboardPage;
